import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Typography, Link, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { PRIMARY_COLOR_HEX } from '../../../../theme';

// Import animations
import stopMessagesAnimation from '../../../../assets/animations/stop-messages-on-mobile.json';
import securedDatabaseAnimation from '../../../../assets/animations/secured-database.json';
import manReceivedSpamMessageAnimation from '../../../../assets/animations/man-received-spam-message.json';
import { useHasCompletedOnboardingSlideshow } from '../../../../app/useHasCompletedOnboardingSlideshow';
import { EventName } from '../../../../services/analytics/providers/analyticsProvider';
import AnalyticsService from '../../../../services/analytics/analyticsService';

export const ONBOARDING_SLIDESHOW_ANIMATIONS = {
  'stop-messages-on-mobile': stopMessagesAnimation,
  'secured-database': securedDatabaseAnimation,
  'man-received-spam-message': manReceivedSpamMessageAnimation,
};

const SHOW_WHERE_FROM_QUESTION = false;

type OnboardingStep = {
  title: string | JSX.Element | null;
  content?: string | JSX.Element | null;
  lottie?: keyof typeof ONBOARDING_SLIDESHOW_ANIMATIONS;
  bullets?: (string | JSX.Element)[];
  showCompanyLogos?: boolean;
  showRatingDisplay?: boolean;
  isQuestion?: boolean;
  questionOptions?: {
    value: string;
    label: string;
  }[];
  onAnswer?: (answer: string) => void;
};

export type OnboardingSlideshowProps = {
  next: () => void;
  shouldShowSlideshow?: boolean;
};

export function useGetOnboardingSlideshowSteps(props: OnboardingSlideshowProps) {
  const { next, shouldShowSlideshow } = props;

  const { t } = useTranslation();
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('xs'));

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});

  const { markAsCompleted } = useHasCompletedOnboardingSlideshow();

  const steps = useMemo<OnboardingStep[]>(
    () => [
      {
        title: t('onboarding.step1.title'),
        content: t('onboarding.step1.content'),
        lottie: 'man-received-spam-message',
      },
      {
        title: t('onboarding.step2.title'),
        content: t('onboarding.step2.content'),
        lottie: 'secured-database',
        bullets: [
          <Trans
            key='onboarding.step1'
            i18nKey='onboarding.step2.bullet1'
            components={{
              privacyLink: (
                <Link
                  href='https://www.trimbox.io/privacy'
                  sx={{
                    color: PRIMARY_COLOR_HEX,
                    textDecoration: 'underline',
                    fontSize: isExtraSmall ? '1rem' : '1rem',
                  }}
                />
              ),
            }}
          />,
          t('onboarding.step2.bullet2') as string,
        ],
      },
      {
        title: (
          <Trans
            i18nKey='onboarding.step3.title'
            values={{
              userCount: new Intl.NumberFormat().format(515687),
              billionCount: new Intl.NumberFormat().format(1.7),
            }}
            components={{
              userCount: (
                <Typography
                  component='span'
                  sx={{
                    color: PRIMARY_COLOR_HEX,
                    fontWeight: 'bold',
                    fontSize: 'inherit',
                  }}
                />
              ),
              billionCount: (
                <Typography
                  component='span'
                  sx={{
                    color: PRIMARY_COLOR_HEX,
                    fontWeight: 'bold',
                    fontSize: 'inherit',
                  }}
                />
              ),
            }}
          />
        ),
        showCompanyLogos: true,
        showRatingDisplay: true,
      },
      ...(SHOW_WHERE_FROM_QUESTION
        ? [
            {
              title: t('onboarding.howDidYouHearAboutUs.title'),
              isQuestion: true,
              questionOptions: [
                { value: 'youtube', label: t('onboarding.howDidYouHearAboutUs.youtube') },
                { value: 'google', label: t('onboarding.howDidYouHearAboutUs.google') },
                { value: 'instagram', label: t('onboarding.howDidYouHearAboutUs.instagram') },
                { value: 'tiktok', label: t('onboarding.howDidYouHearAboutUs.tiktok') },
                { value: 'other', label: t('onboarding.howDidYouHearAboutUs.other') },
              ],
            },
          ]
        : []),
      ...(shouldShowSlideshow
        ? [
            {
              title: t('onboarding.question1.title'),
              isQuestion: true,
              questionOptions: [
                { value: 'daily', label: t('onboarding.question1.option1') },
                { value: 'weekly', label: t('onboarding.question1.option2') },
                { value: 'monthly', label: t('onboarding.question1.option3') },
                { value: 'rarely', label: t('onboarding.question1.option4') },
              ],
            },
            {
              title: t('onboarding.question2.title'),
              isQuestion: true,
              questionOptions: [
                { value: 'one_or_two', label: t('onboarding.question2.option1') },
                { value: 'less_than_five', label: t('onboarding.question2.option2') },
                { value: 'five_to_twenty_five', label: t('onboarding.question2.option3') },
                { value: 'twenty_five_plus', label: t('onboarding.question2.option4') },
              ],
            },
            {
              title: t('onboarding.question3.title'),
              isQuestion: true,
              questionOptions: [
                { value: 'spam_phishing', label: t('onboarding.question3.option1') },
                { value: 'newsletters_subscriptions', label: t('onboarding.question3.option2') },
                { value: 'promotional', label: t('onboarding.question3.option3') },
                { value: 'all_above', label: t('onboarding.question3.option4') },
              ],
            },
            {
              title: t('onboarding.question4.title'),
              isQuestion: true,
              questionOptions: [
                { value: 'save_time', label: t('onboarding.question4.option1') },
                { value: 'stop_unwanted', label: t('onboarding.question4.option2') },
                { value: 'free_storage', label: t('onboarding.question4.option3') },
                { value: 'all_above', label: t('onboarding.question4.option4') },
              ],
            },
          ]
        : []),
    ],
    [t, isExtraSmall, shouldShowSlideshow]
  );

  useEffect(() => {
    const handleBackButton = (e: PopStateEvent) => {
      if (currentStepIndex > 0) {
        e.preventDefault();
        setCurrentStepIndex(currentStepIndex - 1);
      }
    };

    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, [currentStepIndex]);

  const currentAnswer = useMemo(() => answers[currentStepIndex], [answers, currentStepIndex]);
  const currentStep = useMemo(() => steps[currentStepIndex], [steps, currentStepIndex]);

  const handleAnswer = (answer: string) => {
    setAnswers((prev) => ({ ...prev, [currentStepIndex]: answer }));
  };

  const handleNext = useCallback(() => {
    if (currentStep.isQuestion) {
      AnalyticsService.track(EventName.ONBOARDING_SLIDESHOW_ANSWER, {
        answer: currentAnswer,
        step: currentStepIndex,
      });

      AnalyticsService.setPeople({
        [`survey-step-${currentStepIndex}`]: currentAnswer,
      });
    }
    if (currentStepIndex < steps.length - 1) {
      const nextStep = currentStepIndex + 1;
      setCurrentStepIndex(nextStep);
    } else {
      markAsCompleted();
      next();
    }
  }, [currentStepIndex, next, steps.length, markAsCompleted, currentAnswer]);

  return { steps, currentStepIndex, currentStep, answers, currentAnswer, handleAnswer, handleNext };
}
